import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../services/api'
import { Badge, Card, Select, Spinner, Table, EmptyState } from '../../components/ui'
import { formatDateTime } from '../../utils/format'
import { Icon } from '../../components/ui/icons'

interface Lead {
  id: string
  type: 'PATIENT' | 'COMPANY'
  name: string
  email?: string | null
  phone?: string | null
  companyName?: string | null
  message?: string | null
  source: string
  status: 'NEW' | 'CONTACTED' | 'SCHEDULED' | 'CONVERTED' | 'CLOSED'
  createdAt: string
}

const STATUS: Record<string, { label: string; className: string }> = {
  NEW: { label: 'Nuevo', className: 'bg-emerald-100 text-emerald-800' },
  CONTACTED: { label: 'Contactado', className: 'bg-sky-100 text-sky-800' },
  SCHEDULED: { label: 'Agendado', className: 'bg-brand-200 text-brand-800' },
  CONVERTED: { label: 'Paciente', className: 'bg-brand-600 text-white' },
  CLOSED: { label: 'Cerrado', className: 'bg-gray-200 text-gray-600' },
}

export function LeadsPage() {
  const queryClient = useQueryClient()
  const leads = useQuery({
    queryKey: ['leads'],
    queryFn: async () => (await api.get<Lead[]>('/leads')).data,
  })

  const update = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => (await api.patch(`/leads/${id}`, { status })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  })

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-ink">Leads de la web</h1>
      <Card>
        {leads.isLoading ? (
          <Spinner />
        ) : (leads.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin leads todavía. Llegan desde los formularios de la web pública." />
        ) : (
          <Table headers={['Fecha', 'Tipo', 'Contacto', 'Mensaje', 'Estado']}>
            {(leads.data ?? []).map((l) => (
              <tr key={l.id}>
                <td className="px-2 py-2 text-xs text-gray-500">{formatDateTime(l.createdAt)}</td>
                <td className="px-2 py-2">
                  <Badge className={l.type === 'COMPANY' ? 'bg-amber-100 text-amber-800' : 'bg-brand-200 text-brand-800'}>
                    <span className="inline-flex items-center gap-1">
                      <Icon name={l.type === 'COMPANY' ? 'company' : 'person'} className="h-3.5 w-3.5" />
                      {l.type === 'COMPANY' ? 'Empresa' : 'Paciente'}
                    </span>
                  </Badge>
                </td>
                <td className="px-2 py-2">
                  <span className="font-medium">{l.name}</span>
                  {l.companyName && <span className="block text-xs text-gray-500">{l.companyName}</span>}
                  <span className="block text-xs text-gray-500">{[l.phone, l.email].filter(Boolean).join(' · ')}</span>
                </td>
                <td className="max-w-xs px-2 py-2 text-sm text-gray-600">{l.message ?? '—'}</td>
                <td className="px-2 py-2">
                  <Select
                    value={l.status}
                    onChange={(e) => update.mutate({ id: l.id, status: e.target.value })}
                    className="w-36"
                  >
                    {Object.entries(STATUS).map(([v, s]) => (
                      <option key={v} value={v}>{s.label}</option>
                    ))}
                  </Select>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  )
}
